import type { InputBits } from "../sim";
import { padBits, padConfirm, padPause, padRumble } from "./gamepad";
import { KEY_LAYOUTS, KeyboardState } from "./keyboard";

export type Device =
  { readonly kind: "pad"; readonly index: number } | { readonly kind: "keys"; readonly layout: number };

const sameDevice = (a: Device | null, b: Device): boolean =>
  a !== null &&
  a.kind === b.kind &&
  (a.kind === "pad" ? a.index === (b as { index: number }).index : a.layout === (b as { layout: number }).layout);

/**
 * Устройства игроков: геймпады и две раскладки клавиатуры. Игрок занимает место, нажав ✕ (или пробел, Enter)
 * на своём устройстве. Браузер показывает геймпад только после нажатия кнопки на нём.
 */
export class Devices {
  readonly slots: [Device | null, Device | null] = [null, null];
  private readonly keyboard: KeyboardState;
  private pads: (Gamepad | null)[] = [];
  private confirmBefore = new Set<string>();
  private confirmNow = new Set<string>();
  /** Первый опрос только запоминает, что уже зажато: кнопка, зажатая с загрузки (залипшее устройство), — не вход. */
  private polled = false;
  /** Биты раскладок, снятые в опросе кадра (с касаниями короче кадра). */
  private keyBits: number[] = [];

  constructor(target: Window) {
    this.keyboard = new KeyboardState(target);
  }

  poll(): void {
    this.pads = typeof navigator.getGamepads === "function" ? Array.from(navigator.getGamepads()) : [];
    this.confirmBefore = this.confirmNow;
    this.confirmNow = new Set<string>();
    this.pads.forEach((pad, index) => {
      if (pad && padConfirm(pad)) this.confirmNow.add(`pad:${index}`);
    });
    KEY_LAYOUTS.forEach((layout, index) => {
      if (this.keyboard.isDown(layout.confirm)) this.confirmNow.add(`keys:${index}`);
    });
    this.keyBits = KEY_LAYOUTS.map((layout) => this.keyboard.bits(layout));
    this.keyboard.endPoll();
    if (!this.polled) {
      this.polled = true;
      this.confirmBefore = new Set(this.confirmNow);
    }
  }

  /** Какие геймпады видит браузер: имя и раскладка — для индикатора нажатий. */
  padList(): string[] {
    return this.pads.flatMap((pad, index) =>
      pad ? [`${index}: ${pad.id.slice(0, 48)} (${pad.mapping || "нестандартная"})`] : [],
    );
  }

  /** Последняя клавиша, дошедшая до страницы, — для индикатора нажатий. */
  get lastKey(): string {
    return this.keyboard.lastKey;
  }

  /** Слышит ли страница клавиатуру: нет фокуса — нажатия уходят в другое окно. */
  get keyboardFocused(): boolean {
    return this.keyboard.hasFocus;
  }

  private key(device: Device): string {
    return device.kind === "pad" ? `pad:${device.index}` : `keys:${device.layout}`;
  }

  /** Кто нажал ✕ в этот кадр (фронт нажатия). */
  confirmedThisFrame(): Device[] {
    const out: Device[] = [];
    for (const key of this.confirmNow) {
      if (this.confirmBefore.has(key)) continue;
      const [kind, number] = key.split(":");
      out.push(kind === "pad" ? { kind: "pad", index: Number(number) } : { kind: "keys", layout: Number(number) });
    }
    return out;
  }

  /** Занять свободное место; вернуть номер места или -1. */
  claim(device: Device): number {
    if (this.slots.some((slot) => sameDevice(slot, device))) return -1;
    const free = this.slots.indexOf(null);
    if (free >= 0) this.slots[free] = device;
    return free;
  }

  connected(slot: number): boolean {
    const device = this.slots[slot];
    if (!device) return false;
    return device.kind === "keys" || Boolean(this.pads[device.index]?.connected);
  }

  bits(slot: number): InputBits {
    const device = this.slots[slot];
    if (!device) return 0;
    if (device.kind === "keys") return this.keyBits[device.layout] ?? 0;
    const pad = this.pads[device.index];
    return pad ? padBits(pad) : 0;
  }

  pausePressed(): boolean {
    return this.pads.some((pad) => pad !== null && padPause(pad)) || this.keyboard.isDown("Escape");
  }

  rumble(slot: number, strong: number, weak: number, duration: number): void {
    const device = this.slots[slot];
    const pad = device?.kind === "pad" ? this.pads[device.index] : null;
    if (pad) padRumble(pad, strong, weak, duration);
  }

  label(slot: number): string {
    const device = this.slots[slot];
    if (!device) return "—";
    if (device.kind === "keys") return `клавиатура: ${KEY_LAYOUTS[device.layout]?.name ?? ""}`;
    return this.pads[device.index]?.id.split("(")[0]?.trim() ?? "геймпад";
  }
}
